import type { IOrganizationSettingsCustomer } from "@/api";
import { readScopesEnabled } from "@/api";
import { openHelpBeacon } from "@/lib/helpscout";
import type { LucideIcon } from "lucide-react";
import {
    BookCheck,
    BookOpen,
    CircleHelp,
    FileSearch2,
    FolderOpen,
    House,
    LibraryBig,
    MessageSquare,
    Users,
} from "lucide-react";

export type FrontendSection = "Main" | "Resources" | "Support";

export interface FrontendRoute {
    name: string;
    path: string;
    icon: LucideIcon;
    section: FrontendSection;
    searchParams?: Record<string, string>;
    alsoMatchPaths?: string[];
    isExternal?: boolean;
    href?: string;
    featureFlag?: keyof IOrganizationSettingsCustomer;
    requiresScopes?: true;
    onClick?: () => void;
}

/**
 * Whether a nav item renders for an organization.
 *
 * The two gates differ on an absent value: a `featureFlag` names a setting the
 * org must have switched ON, so absent hides the item, while scopes are on
 * until an org turns them off.
 */
export function isRouteVisible(
    route: FrontendRoute,
    settings: IOrganizationSettingsCustomer
): boolean {
    if (route.featureFlag && !settings[route.featureFlag]) {
        return false;
    }
    return !route.requiresScopes || readScopesEnabled(settings);
}

export const FRONTEND_ROUTES: readonly FrontendRoute[] = [
    { name: "Home", path: "home", icon: House, section: "Main" },
    // Clients is intentionally a Main-section item at position 2 (Client Hub
    // promotes client-centric work to the primary workflow); don't demote it
    // back to Resources in a reorder.
    { name: "Clients", path: "clients", icon: Users, section: "Main" },
    {
        name: "Ask Polly",
        path: "chat",
        icon: MessageSquare,
        section: "Main",
        featureFlag: "ai_chat_enabled",
    },
    // Knowledge Base sits directly under Ask Polly: it's the curated guidelines
    // / best-practices corpus that powers Polly's knowledge_base_search, so it
    // reads as "what Polly knows" and is treated as a primary, actively-curated
    // surface rather than a passive library. It still renders the
    // DocumentLibraryContent surface (is_knowledge_base flag) at a sub-route of
    // document-library, so the Document Library entry below stays anchored at
    // its /documents leaf to avoid a double active-state highlight.
    {
        name: "Knowledge Base",
        path: "document-library/knowledge-base",
        icon: BookOpen,
        section: "Main",
    },
    {
        name: "Scopes",
        path: "document-library/documents",
        icon: FileSearch2,
        section: "Main",
        searchParams: { type_filter: "scopes" },
        alsoMatchPaths: ["/organizations/scopes"],
        requiresScopes: true,
    },
    {
        name: "Document Library",
        // Anchored at the /documents leaf (not the bare parent) so the prefix
        // active-state match stays inside the documents view and does NOT light
        // up when the sibling Knowledge Base sub-route is active. The index
        // route redirects bare /document-library -> /documents, so navigation
        // is unchanged.
        path: "document-library/documents",
        icon: FolderOpen,
        section: "Resources",
    },
    {
        name: "Clause Library",
        path: "clause-library",
        icon: LibraryBig,
        section: "Resources",
        requiresScopes: true,
    },
    {
        name: "Checklists",
        path: "checklists",
        icon: BookCheck,
        section: "Resources",
        requiresScopes: true,
    },
    {
        name: "Help",
        path: "",
        icon: CircleHelp,
        section: "Resources",
        onClick: openHelpBeacon,
    },
];

export const SECONDARY_NAV: readonly FrontendRoute[] = [];

export const MAIN_ROUTES = FRONTEND_ROUTES.filter((r) => r.section === "Main");

export const RESOURCE_ROUTES = FRONTEND_ROUTES.filter(
    (r) => r.section === "Resources"
);
